import { Button } from "components/DesignSystem/Button/Button";
import { BareInput } from "components/DesignSystem/TextInput/TextInput";
import {
  ToggleGroup,
  ToggleGroupItem,
} from "components/DesignSystem/ToggleGroup/ToggleGroup";
import { TableUI } from "components/design/TableUI";
import ToolTip from "components/design/toolTip";
import { Info } from "components/icons/Info";
import { TrashSimple } from "components/icons/TrashSimple";
import { MMM_YYYY } from "constants/date";
import { RND_SECTION_TYPE } from "constants/financialClosing";
import dayjs from "dayjs";
import { useRef, useState } from "react";
import { debounce } from "utils/debouncing";
import {
  getCoreRowModel,
  createColumnHelper,
  useReactTable,
} from "react-table-8.10.7";
import {
  RnDBifurcationFieldName,
  RnDBifurcationFieldValue,
  RnDVendor,
} from "store/apis/financialClosing";

type SectionType = (typeof RND_SECTION_TYPE)[keyof typeof RND_SECTION_TYPE];

type Location = "US" | "NON_US";

type VendorFormValue = {
  vendor_id: string;
  vendor_name: string;
  total_amount: string;
  location: Location;
  rd_percentage: string;
  remarks: string;
};

type UpdateField = (
  vendorId: string,
  field: RnDBifurcationFieldName,
  value: RnDBifurcationFieldValue
) => void;

type RnDTableMeta = {
  isFormEditable: boolean;
  previousMonthMap: Record<string, RnDVendor>;
  previousMonthDate: string | null;
  updateField: UpdateField;
  onLocationChange: (index: number, location: Location) => void;
  onRdPercentageChange: (index: number, value: string) => void;
  onRemarksChange: (index: number, value: string) => void;
  removeVendor: (index: number) => void;
};

const DEBOUNCE_MS = 500;

const getPreviousMonthTooltip = (
  prev: RnDVendor,
  previousMonthDate: string | null
) => {
  const monthLabel = previousMonthDate
    ? dayjs(previousMonthDate).format(MMM_YYYY)
    : "Last month";
  return (
    <div className="t-flex t-flex-col t-gap-1 t-text-left">
      <span>{monthLabel} -</span>
      {prev.is_us && <span>US R&D: {prev.us_rd_percentage}%</span>}
      {prev.is_non_us && <span>Non-US R&D: {prev.non_us_rd_percentage}%</span>}
      {prev.remarks && <span>Remarks: {prev.remarks}</span>}
      {!prev.is_us && !prev.is_non_us && <span>No R&D allocation</span>}
    </div>
  );
};

const formatAmount = (value: string) => {
  const num = Math.round(Math.abs(Number(value)));
  return `$${num.toLocaleString("en-US")}`;
};

const columnHelper = createColumnHelper<VendorFormValue>();

const columns = [
  columnHelper.accessor("vendor_name", {
    id: "Vendor",
    header: "Vendor",
    size: 22,
    cell: (info) => {
      const { previousMonthMap, previousMonthDate } = info.table.options
        .meta as RnDTableMeta;
      const prevEntry = previousMonthMap[info.row.original.vendor_id];
      return (
        <div className="t-flex t-items-center t-gap-1">
          <span className="t-text-subtext t-font-medium t-text-text-60 t-truncate">
            {info.getValue()}
          </span>
          {prevEntry && (
            <ToolTip
              text={getPreviousMonthTooltip(prevEntry, previousMonthDate)}
              side="top"
            >
              <span className="t-inline-flex t-text-text-30 t-shrink-0">
                <Info />
              </span>
            </ToolTip>
          )}
        </div>
      );
    },
  }),
  columnHelper.accessor("total_amount", {
    id: "Expense",
    header: () => (
      <ToolTip
        text="Sum of all transactions for this vendor in the closing period"
        side="top"
      >
        <span className="t-flex t-items-center t-gap-1 t-cursor-default">
          Expense
          <Info />
        </span>
      </ToolTip>
    ),
    size: 13,
    cell: (info) => (
      <span className="t-text-subtext t-text-text-60">
        {formatAmount(info.getValue())}
      </span>
    ),
  }),
  columnHelper.accessor("location", {
    id: "Location",
    header: () => (
      <ToolTip
        text="Choose the location where this vendor's R&D work was performed — US or outside the US."
        side="top"
      >
        <span className="t-flex t-items-center t-gap-1 t-cursor-default">
          Location
          <Info />
        </span>
      </ToolTip>
    ),
    size: 18,
    cell: (info) => {
      const { isFormEditable, onLocationChange } = info.table.options
        .meta as RnDTableMeta;
      const index = info.row.index;
      return (
        <ToggleGroup
          value={info.getValue()}
          size="small"
          variant="secondary"
          disabled={!isFormEditable}
          onValueChange={(value) => {
            if (!value) return;
            onLocationChange(index, value as Location);
          }}
        >
          <ToggleGroupItem
            value="US"
            className="t-whitespace-nowrap t-text-caption"
          >
            US
          </ToggleGroupItem>
          <ToggleGroupItem
            value="NON_US"
            className="t-whitespace-nowrap t-text-caption"
          >
            Non-US
          </ToggleGroupItem>
        </ToggleGroup>
      );
    },
  }),
  columnHelper.accessor("rd_percentage", {
    id: "RdPercent",
    header: () => (
      <span className="t-flex t-items-center t-gap-1 t-cursor-default t-whitespace-nowrap">
        R&D Percentage
        <ToolTip
          text="Percentage of this vendor's spend that qualifies as R&D."
          side="top"
        >
          <span className="t-inline-flex">
            <Info />
          </span>
        </ToolTip>
      </span>
    ),
    size: 20,
    cell: (info) => {
      const { isFormEditable, onRdPercentageChange } = info.table.options
        .meta as RnDTableMeta;
      const index = info.row.index;
      const rdValue = Number(info.getValue());
      const isUsLocation = info.row.original.location === "US";
      return (
        <div className="t-flex t-flex-col t-gap-1">
          <BareInput
            customSize="small"
            type="number"
            min="0"
            max="100"
            value={info.getValue()}
            onChange={(e) => onRdPercentageChange(index, e.target.value)}
            disabled={!isFormEditable || isUsLocation}
          />
          <span
            className={`t-text-xs t-text-text-30 ${
              info.getValue() !== "" ? "" : "t-invisible"
            }`}
          >
            {`Non-R&D: ${100 - rdValue}%`}
          </span>
        </div>
      );
    },
  }),
  columnHelper.accessor("remarks", {
    id: "Remarks",
    header: "Remarks",
    size: 22,
    cell: (info) => {
      const { isFormEditable, onRemarksChange } = info.table.options
        .meta as RnDTableMeta;
      const index = info.row.index;
      return (
        <div className="t-flex t-flex-col t-gap-1">
          <BareInput
            customSize="small"
            type="text"
            value={info.getValue()}
            onChange={(e) => onRemarksChange(index, e.target.value)}
            disabled={!isFormEditable}
          />
          <span className="t-text-xs t-invisible">-</span>
        </div>
      );
    },
  }),
  columnHelper.display({
    id: "actions",
    header: "",
    size: 5,
    cell: (info) => {
      const { isFormEditable, removeVendor } = info.table.options
        .meta as RnDTableMeta;
      const index = info.row.index;
      if (!isFormEditable) return null;
      return (
        <div className="t-flex t-flex-col t-gap-1">
          <Button
            type="button"
            customType="ghost_icon"
            size="xsmall"
            onClick={() => removeVendor(index)}
          >
            <span className="t-text-text-30">
              <TrashSimple />
            </span>
          </Button>
          <span className="t-text-xs t-invisible">-</span>
        </div>
      );
    },
  }),
];

const EditableVendorTable = ({
  vendors,
  isFormEditable,
  previousMonthMap,
  previousMonthDate,
  updateField,
  onLocationChange,
  onRdPercentageChange,
  onRemarksChange,
  onRemoveVendor,
}: {
  vendors: VendorFormValue[];
  isFormEditable: boolean;
  previousMonthMap: Record<string, RnDVendor>;
  previousMonthDate: string | null;
  updateField: UpdateField;
  onLocationChange: (index: number, location: Location) => void;
  onRdPercentageChange: (index: number, value: string) => void;
  onRemarksChange: (index: number, value: string) => void;
  onRemoveVendor: (index: number) => void;
}) => {
  const table = useReactTable({
    data: vendors,
    columns,
    getCoreRowModel: getCoreRowModel(),
    defaultColumn: { minSize: 5 },
    meta: {
      isFormEditable,
      previousMonthMap,
      previousMonthDate,
      updateField,
      onLocationChange,
      onRdPercentageChange,
      onRemarksChange,
      removeVendor: onRemoveVendor,
    },
  });

  return (
    <TableUI
      table={table}
      sticky={false}
      verticalAlign="top"
      sizeType="percent"
      layout="fixed"
    />
  );
};

const toLocation = (v: RnDVendor): Location => (v.is_non_us ? "NON_US" : "US");

const toRdPercentage = (v: RnDVendor): string => {
  if (v.is_us) return v.us_rd_percentage;
  if (v.is_non_us) return v.non_us_rd_percentage;
  return "0";
};

const toVendorFormValues = (vendors: RnDVendor[]): VendorFormValue[] =>
  vendors.map((v) => ({
    vendor_id: v.vendor_id,
    vendor_name: v.vendor_name,
    total_amount: v.total_amount ?? "0",
    location: toLocation(v),
    rd_percentage: toRdPercentage(v),
    remarks: v.remarks,
  }));

const rdPercentageField = (location: Location): RnDBifurcationFieldName =>
  location === "US" ? "us_rd_percentage" : "non_us_rd_percentage";

type Props = {
  vendors: RnDVendor[];
  previousMonthReference: RnDVendor[];
  previousMonthDate?: string | null;
  sectionType: SectionType;
  isFormEditable: boolean;
  updateField: UpdateField;
};

export const RnDBifurcation = ({
  vendors,
  previousMonthReference,
  previousMonthDate = null,
  sectionType,
  isFormEditable,
  updateField,
}: Props) => {
  const [rows, setRows] = useState<VendorFormValue[]>(() =>
    toVendorFormValues(vendors ?? [])
  );

  const debouncedUpdaters = useRef<Record<string, typeof updateField>>({});

  const previousMonthMap = (previousMonthReference ?? []).reduce<
    Record<string, RnDVendor>
  >((acc, vendor) => {
    acc[vendor.vendor_id] = vendor;
    return acc;
  }, {});

  const scheduleFieldUpdate = (
    debounceKey: string,
    vendorId: string,
    field: RnDBifurcationFieldName,
    value: RnDBifurcationFieldValue
  ) => {
    debouncedUpdaters.current[debounceKey] ??= debounce(
      updateField,
      DEBOUNCE_MS
    );
    debouncedUpdaters.current[debounceKey](vendorId, field, value);
  };

  const handleLocationChange = (index: number, location: Location) => {
    const vendor = rows[index];
    if (vendor.location === location) return;

    const rdPercentage = location === "US" ? "0" : "100";
    setRows((prev) =>
      prev.map((row, i) =>
        i === index ? { ...row, location, rd_percentage: rdPercentage } : row
      )
    );

    const oldField: RnDBifurcationFieldName =
      location === "US" ? "is_non_us" : "is_us";
    const newField: RnDBifurcationFieldName =
      location === "US" ? "is_us" : "is_non_us";

    updateField(vendor.vendor_id, oldField, false);
    updateField(vendor.vendor_id, newField, true);
    updateField(
      vendor.vendor_id,
      rdPercentageField(location),
      Number(rdPercentage)
    );
  };

  const handleRdPercentageChange = (index: number, value: string) => {
    const vendor = rows[index];
    setRows((prev) =>
      prev.map((row, i) =>
        i === index ? { ...row, rd_percentage: value } : row
      )
    );

    if (value === "") return;
    scheduleFieldUpdate(
      `${vendor.vendor_id}:rd_percentage`,
      vendor.vendor_id,
      rdPercentageField(vendor.location),
      Number(value)
    );
  };

  const handleRemarksChange = (index: number, value: string) => {
    const vendor = rows[index];
    setRows((prev) =>
      prev.map((row, i) => (i === index ? { ...row, remarks: value } : row))
    );

    scheduleFieldUpdate(
      `${vendor.vendor_id}:remarks`,
      vendor.vendor_id,
      "remarks",
      value
    );
  };

  const handleRemoveVendor = (index: number) => {
    const vendor = rows[index];
    setRows((prev) => prev.filter((_, i) => i !== index));
    updateField(vendor.vendor_id, "is_excluded", true);
  };

  if ((vendors ?? []).length === 0) {
    return (
      <p className="t-text-body-sm t-text-text-30 t-py-4">
        No vendors found for this period
      </p>
    );
  }

  return (
    <div className="t-flex t-flex-col t-gap-4">
      <EditableVendorTable
        vendors={rows}
        isFormEditable={isFormEditable}
        previousMonthMap={previousMonthMap}
        previousMonthDate={previousMonthDate}
        updateField={updateField}
        onLocationChange={handleLocationChange}
        onRdPercentageChange={handleRdPercentageChange}
        onRemarksChange={handleRemarksChange}
        onRemoveVendor={handleRemoveVendor}
      />
    </div>
  );
};
